import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileTextIcon, UserIcon, BuildingIcon, PillIcon, CameraIcon, CheckCircleIcon, ShieldCheckIcon, ArrowRightIcon, ArrowLeftIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function TransferPrescription() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  if (submitted) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="min-h-[80vh] flex items-center justify-center px-4" data-id="element-556" data-mp="1hs0atvc1topb">
        <div className="max-w-md w-full text-center bg-white p-12 rounded-3xl shadow-2xl border border-slate-100" data-id="element-557" data-mp="3ohf0d9r3c3yy">
          <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-8" data-id="element-558" data-mp="3s7t4gqiwola1">
            <CheckCircleIcon className="w-12 h-12" data-id="element-559" data-mp="3nf9t807oy28e" />
          </div>
          <h1 className="text-3xl font-black text-slate-900 mb-4 uppercase tracking-tight" data-id="element-560" data-mp="3igyizodhhppn">
            Transfer Request Sent!
          </h1>
          <p className="text-slate-500 mb-10 leading-relaxed" data-id="element-561" data-mp="3ehzfm87ei10b">
            Thank you for choosing Daily Care Pharmacy. Our staff will contact
            your current pharmacy and handle the transfer for you. We'll notify
            you when your prescriptions are ready.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 bg-clinical-blue text-white font-black py-4 px-8 rounded-xl hover:bg-blue-700 transition-all uppercase tracking-widest text-xs" data-id="element-562" data-mp="5yrvyiwl0car">
            Return Home
          </Link>
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="bg-slate-50 min-h-screen py-12 md:py-20" data-id="element-563" data-mp="2ltk3pujkfyt7">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-564" data-mp="2s6endra762f1">
        <div className="mb-12 text-center" data-id="element-565" data-mp="2e8hkye92b1sg">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-clinical-blue/5 text-clinical-blue rounded-full text-[10px] font-bold uppercase tracking-widest mb-6" data-id="element-566" data-mp="2e7ez4kqljpje">
            Prescription Services
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-4 uppercase tracking-tighter" data-id="element-567" data-mp="24gbw3de3x0hh">
            Transfer Your Prescription
          </h1>
          <p className="text-slate-600" data-id="element-568" data-mp="1ula5ed46nqcs">
            Switching is easy. We'll handle the communication with your old
            pharmacy.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-12 flex justify-between items-center relative" data-id="element-569" data-mp="1lklrrjipbs0f">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-200 z-0" data-id="element-570" data-mp="1qivmjt0ikqli"></div>
          <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-clinical-blue z-0 transition-all duration-500" style={{
          width: `${(step - 1) / 2 * 100}%`
        }} data-id="element-571" data-mp="16vsg6cged9io"></div>

          {[1, 2, 3].map(s => <div key={s} className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-500 ${step >= s ? 'bg-clinical-blue text-white shadow-lg shadow-clinical-blue/30' : 'bg-white text-slate-400 border-2 border-slate-200'}`} data-id="element-572" data-mp="1bvomwjghbf1x">
              {step > s ? <CheckCircleIcon className="w-5 h-5" data-id="element-573" data-mp="1zk7vz17crvv2" /> : s}
            </div>)}
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden" data-id="element-574" data-mp="1unhdy8bzmq70">
          <div className="p-8 md:p-12" data-id="element-575" data-mp="23murbc38fyx0">
            <AnimatePresence mode="wait" data-id="element-576" data-mp="23nw0tc4lodcm">
              {step === 1 && <motion.div key="step1" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-577" data-mp="1y6z011z49dn">
                  <div className="flex items-center gap-3 mb-8" data-id="element-578" data-mp="3f5ryo5d4soat">
                    <UserIcon className="w-6 h-6 text-clinical-blue" data-id="element-579" data-mp="3k2icem1gr7t2" />
                    <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight" data-id="element-580" data-mp="3o4vi5ag7n5pu">
                      Patient Information
                    </h2>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-581" data-mp="3t1lvvr4jlp83">
                    <div className="space-y-2" data-id="element-582" data-mp="13dv0p0xssi7m">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-583" data-mp="18dr2ctj0vxk0">
                        Full Name
                      </label>
                      <input required type="text" placeholder="John Doe" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-584" data-mp="1igwefn3246wp" />
                    </div>
                    <div className="space-y-2" data-id="element-585" data-mp="zsrgiw92zg5t">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-586" data-mp="usd6j8nx5w55">
                        Date of Birth
                      </label>
                      <input required type="date" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-587" data-mp="putu8eas7tfu" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-588" data-mp="dhzm2qsuywqj">
                    <div className="space-y-2" data-id="element-589" data-mp="ia146tcpjhoh">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-590" data-mp="iasmx3l7hkxc">
                        Phone Number
                      </label>
                      <input required type="tel" placeholder="(555) 000-0000" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-591" data-mp="8kpvh7tqgf14" />
                    </div>
                    <div className="space-y-2" data-id="element-592" data-mp="s6m8tdkteqrs">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-593" data-mp="s9i7x58eflht">
                        Email Address
                      </label>
                      <input required type="email" placeholder="john@example.com" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-594" data-mp="3czhat0al74dk" />
                    </div>
                  </div>
                </motion.div>}

              {step === 2 && <motion.div key="step2" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-595" data-mp="2lqxlpt2c4kqe">
                  <div className="flex items-center gap-3 mb-8" data-id="element-596" data-mp="1y0tk1r8piv21">
                    <BuildingIcon className="w-6 h-6 text-clinical-blue" data-id="element-597" data-mp="1y0jug4rkqpwv" />
                    <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight" data-id="element-598" data-mp="1cisd9fucp7y5">
                      Current Pharmacy
                    </h2>
                  </div>

                  <div className="space-y-2" data-id="element-599" data-mp="1mfevdwvalkwh">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-600" data-mp="1mhrbe4hhpl6w">
                      Pharmacy Name
                    </label>
                    <input required type="text" placeholder="e.g. CVS, Walgreens, Local Pharmacy" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-601" data-mp="1vfl6jc3iq8sh" />
                  </div>

                  <div className="space-y-2" data-id="element-602" data-mp="2f5n46y3xusye">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-603" data-mp="2j5xbce3gvpeu">
                      Pharmacy Phone Number
                    </label>
                    <input required type="tel" placeholder="(555) 000-0000" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-604" data-mp="7jwrrxihe3e1" />
                  </div>

                  <div className="space-y-2" data-id="element-605" data-mp="r61kq2x2lyjl">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-606" data-mp="m7qa3pyybk0b">
                      Pharmacy Address
                    </label>
                    <textarea required rows={3} placeholder="Street, City, State, Zip" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all resize-none" data-id="element-607" data-mp="heorb6vmj0bg"></textarea>
                  </div>
                </motion.div>}

              {step === 3 && <motion.div key="step3" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-608" data-mp="2pcrk1cob93aa">
                  <div className="flex items-center gap-3 mb-8" data-id="element-609" data-mp="3myffnvcxzhkr">
                    <PillIcon className="w-6 h-6 text-clinical-blue" data-id="element-610" data-mp="3n0rva1v89fun" />
                    <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight" data-id="element-611" data-mp="3i0x3lw3mom8j">
                      Prescription Details
                    </h2>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-612" data-mp="3rytf4p0h9j76">
                    <div className="space-y-2" data-id="element-613" data-mp="egohdb3odhg8">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-614" data-mp="ehzmbe8uwv8x">
                        Medication Name
                      </label>
                      <input required type="text" placeholder="e.g. Amoxicillin" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-615" data-mp="5gt3uzmndad1" />
                    </div>
                    <div className="space-y-2" data-id="element-616" data-mp="3ivpdq1nlj3m8">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-617" data-mp="3istg6efnugyb">
                        Rx Number (Optional)
                      </label>
                      <input type="text" placeholder="e.g. 1234567" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-618" data-mp="38x8l7090egy2" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-619" data-mp="1aocdp3fajor3">
                    <div className="space-y-2" data-id="element-620" data-mp="15ogb98m8s5dn">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-621" data-mp="15pren7brzb48">
                        Dosage
                      </label>
                      <input required type="text" placeholder="e.g. 500mg" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-622" data-mp="1ycc3a00v9943" />
                    </div>
                    <div className="space-y-2" data-id="element-623" data-mp="cyznwpdhbeoa">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-624" data-mp="cy7pl6shz2ub">
                        Quantity
                      </label>
                      <input required type="text" placeholder="e.g. 30 tablets" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-625" data-mp="7vqg7w620yro" />
                    </div>
                  </div>

                  <div className="pt-4" data-id="element-626" data-mp="qjlm6tvfd8a8">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-3" data-id="element-627" data-mp="vk9mk65jn1h4">
                      Upload Prescription Photo (Optional)
                    </label>
                    <div className="border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center hover:border-clinical-blue transition-colors cursor-pointer group" data-id="element-628" data-mp="2xbmiw6tg411k">
                      <CameraIcon className="w-8 h-8 text-slate-300 mx-auto mb-3 group-hover:text-clinical-blue transition-colors" data-id="element-629" data-mp="32ctoq0c12q5l" />
                      <p className="text-sm text-slate-500" data-id="element-630" data-mp="rnm1ddmu0g6j">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest font-bold" data-id="element-631" data-mp="15mlfhrcpxcog">
                        JPG, PNG up to 10MB
                      </p>
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-xl flex gap-3 items-start" data-id="element-632" data-mp="cwd8mvnmuep6">
                    <ShieldCheckIcon className="w-5 h-5 text-clinical-blue flex-shrink-0 mt-0.5" data-id="element-633" data-mp="8yp739rfpnob" />
                    <p className="text-[10px] text-slate-600 leading-relaxed" data-id="element-634" data-mp="mpkai6j5zcki">
                      <span className="font-bold text-clinical-blue uppercase tracking-widest block mb-1" data-id="element-635" data-mp="hwr5dhjsa27w">
                        HIPAA Compliance Notice
                      </span>
                      Your information is protected by federal law. By
                      submitting this form, you authorize Daily Care Pharmacy to
                      contact your current pharmacy to facilitate the transfer
                      of your prescriptions.
                    </p>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 flex justify-between items-center" data-id="element-636" data-mp="1wvewirpksdjy">
            {step > 1 ? <button type="button" onClick={prevStep} className="flex items-center gap-2 text-slate-500 hover:text-slate-900 font-bold uppercase tracking-widest text-[10px] transition-colors" data-id="element-637" data-mp="2kid9vgief5dn">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-638" data-mp="2fk1wivswacqo" /> Back
              </button> : <div data-id="element-639" data-mp="z5tsahbzsq3u"></div>}

            {step < 3 ? <button type="button" onClick={nextStep} className="bg-clinical-blue text-white font-black py-3 px-8 rounded-xl hover:bg-blue-700 transition-all uppercase tracking-widest text-[10px] flex items-center gap-2 shadow-lg shadow-clinical-blue/20" data-id="element-640" data-mp="145oieiny1bnu">
                Continue <ArrowRightIcon className="w-4 h-4" data-id="element-641" data-mp="bahzo0kw87r6" />
              </button> : <button type="submit" className="bg-clinical-blue text-white font-black py-3 px-8 rounded-xl hover:bg-blue-700 transition-all uppercase tracking-widest text-[10px] flex items-center gap-2 shadow-lg shadow-clinical-blue/20" data-id="element-642" data-mp="6bdqh9xyya0u">
                Submit Transfer Request
              </button>}
          </div>
        </form>
      </div>
    </motion.div>;
}